import { tokens } from '@kdk/shared/theme';
import type { SxProps, Theme } from '@mui/material';

/** スクロールバーの太さ(px)。トラックもつまみもこの太さ。 */
export const SCROLLBAR_SIZE = 10;

/** sx の「オブジェクト形式」だけ(配列や関数でないもの)。sx の配列に並べて合成できる。 */
export type SxObject = Exclude<
  SxProps<Theme>,
  ReadonlyArray<unknown> | ((theme: Theme) => unknown)
>;

const SIZE = `${SCROLLBAR_SIZE}px`;

export type ScrollbarColors = { track: string; thumb: string; thumbHover: string };

/** 既定は表のスクロールバーの色(theme の `tokens.scrollbar`)。 */
const TABLE_SCROLLBAR_COLORS: ScrollbarColors = tokens.scrollbar;

/**
 * サイドバー用。サイドバーは(ライトでも)濃い背景なので、表用の明るいトラックは使わず、
 * サイドバーの配色トークンから取る。ダーク・ライトのどちらでも背景になじみ、つまみは見える。
 */
export const SIDEBAR_SCROLLBAR_COLORS: ScrollbarColors = {
  track: tokens.sidebarHover,
  thumb: tokens.sidebarLine2,
  thumbHover: tokens.sidebarMu,
};

/**
 * SimpleBar のスクロールバーを、常時表示のカプセル形にする。
 * つまみは `.simplebar-scrollbar:before` に描かれる(SimpleBar 既定は透明なので opacity を強制する)。
 * 色は theme のトークンから取り、色の直書きはしない。
 * スクロールバーは、その向きにあふれるときだけ出す(空のトラックは描かない)。出ているあいだだけ、
 * 内容の下(横)・右(縦)に余白を足して、スクロールバーが内容に重ならないようにする。
 */
export const simplebarLookSx = (
  horizontal: boolean,
  vertical: boolean,
  colors: ScrollbarColors = TABLE_SCROLLBAR_COLORS,
): SxObject => ({
  '& .simplebar-track': { borderRadius: 999, backgroundColor: colors.track },
  '& .simplebar-track.simplebar-horizontal': {
    bottom: 0,
    height: SIZE,
  },
  // 縦のスクロールバーが出ているときだけ、角で重ならないように横を縮める(逆も同様)
  ...(vertical && {
    '& .simplebar-scrollable-y .simplebar-track.simplebar-horizontal': { right: SIZE },
  }),
  '& .simplebar-track.simplebar-vertical': {
    right: 0,
    width: SIZE,
  },
  ...(horizontal && {
    '& .simplebar-scrollable-x .simplebar-track.simplebar-vertical': { bottom: SIZE },
  }),
  '& .simplebar-scrollbar:before': {
    top: '1px',
    bottom: '1px',
    left: '1px',
    right: '1px',
    opacity: 1,
    borderRadius: 999,
    backgroundColor: colors.thumb,
    transition: 'none',
  },
  '& .simplebar-scrollbar.simplebar-visible:before': { opacity: 1 },
  '& .simplebar-track:hover .simplebar-scrollbar:before, & .simplebar-dragging .simplebar-scrollbar:before':
    { backgroundColor: colors.thumbHover },
  // 内容の下(横)・右(縦)の余白は、SimpleBar の root の padding で指定する。SimpleBar はこれを内容側へ移して
  // 内側の寸法を調整する(content や content-wrapper に直接付けても、inline style で上書きされるか、
  // 縦に伸ばす画面で 10px 分の余計な縦スクロールが生まれる)。
  // 余白はその向きにあふれて、スクロールバーが実際に出ているときだけ(SimpleBar が root に
  // `simplebar-scrollable-x/y` を付け外しする)。あふれない表の下・右に空の帯を残さない。
  ...(horizontal && { '& [data-simplebar].simplebar-scrollable-x': { paddingBottom: SIZE } }),
  ...(vertical && { '& [data-simplebar].simplebar-scrollable-y': { paddingRight: SIZE } }),
});

/**
 * 高さの鎖: SimpleBar の内側は wrapper → mask → offset → content-wrapper → content と重なっていて、
 * どれか 1 つでも高さが切れると、つまみの位置が狂ったり中身がページを押し広げたりする。
 * `fill` のときだけ、全層を親の高さに揃える。
 */
export const simplebarHeightChainSx: SxObject = {
  '& .simplebar-wrapper, & .simplebar-mask, & .simplebar-offset, & .simplebar-content-wrapper': {
    height: '100%',
    maxHeight: '100%',
  },
  '& .simplebar-content': { height: '100%', minHeight: 0 },
};
